import {
  Component,
  ViewChild,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  AfterViewInit,
  HostListener
} from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as showdown from 'showdown';
import { Subscription, firstValueFrom } from 'rxjs';

import { ChatFireService } from 'src/app/gen2ai/chatfire.service';
import { SideMenusService } from '../../core/side-menus/side-menus.service';
import { Message } from '../../models/chat.model';
import { showdownMarkdownClassesExtension } from '../../shared/markdown/showdown-md-classes.extension';
import { AIFireService } from '../../gen2ai/fire.service';

import mkPacmanClient from 'pacman-client';

// dialogs
import { StabilityComponent } from '../dialogs/stability/stability.component';
import { DalEComponent } from '../dialogs/dal-e/dal-e.component';
import { AiCatalogComponent } from '../dialogs/ai-catalog/ai-catalog.component';

// Interface for placeholder options with paired text, logo, and name
interface PlaceholderOption {
  text: string;
  logoUrl: string;
  name: string;
}

@Component({
  selector: 'app-clari-media',
  templateUrl: './clari-media.component.html',
  styleUrls: ['./clari-media.component.scss']
})
export class ClariMediaComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup;
  o3form: FormGroup;

  prompt = '';
  selectedModel$: any;
  inputText = '';
  selectedFile?: File;
  selectedImagePreview: string | null = null;
  theSummary: string | null = null;

  @Input() id!: string;

  currentMessages: Message[] = [];
  converter: showdown.Converter;
  convertedMarkdown: string = '';

  isLoading = false;
  userInput = '';
  private userScrolledUp = false;

  // Video-specific properties
  uploadImageFile: File | null = null;
  uploadImagePreviewUrl: string | null = null;
  generating = false;
  uiProgress = 0;
  private videoUiRunId: string | null = null;
  private videoRunStartMs = 0;
  private videoProgressInterval: any = null;

  @ViewChild('mainSidenav') mainSidenav: MatSidenav;
  showScrollToBottom = false;
  loading: boolean = false;
  markdownOutput: string = '';
  error: string | null = null;
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('chatTextarea') chatTextarea!: ElementRef<HTMLTextAreaElement>;
  showMainSubtitle = true;
  menuOpen: boolean = false;
  selectedModelName: string = 'Stability 3.5';
  showVideoGeneratorMenu = false;
  @ViewChild('menuContainer') menuContainer!: ElementRef;

  messageHistory: string[] = [];
  historyIndex: number = -1;
  isRepeatPromptActive = false;

  // Placeholder options with paired text, logo URLs, and names for 6 media models
  placeholderOptions: PlaceholderOption[] = [
    {
      text: "Stability 3.5 for vivid diffusion art.",
      logoUrl: "assets/logos/mistral.svg",
      name: "Stability 3.5"
    },
    {
      text: "DALL·E creates photorealistic images fast.",
      logoUrl: "assets/logos/openai.svg",
      name: "DALL·E"
    },
    {
      text: "Gemini Image brings Google-powered visuals.",
      logoUrl: "assets/logos/google.svg",
      name: "Gemini Image"
    },
    {
      text: "Veo 3.1 crafts cinematic video shots.",
      logoUrl: "assets/logos/google.svg",
      name: "Veo 3.1"
    },
    {
      text: "CogXVid delivers high-quality video generation.",
      logoUrl: "assets/logos/zai.svg",
      name: "CogXVid"
    },
    {
      text: "Veo 3.1 crafts cinematic video shots.",
      logoUrl: "assets/logos/google.svg",
      name: "Veo 3.1"
    }
  ];

  // Current logo for display
  currentLogo: string = this.placeholderOptions[0].logoUrl;
  logoFading = false;

  // Get current placeholder option
  get currentPlaceholderOption(): PlaceholderOption | undefined {
    return this.placeholderOptions[this.placeholderIndex];
  }

  // Get model name from current placeholder option
  get currentModelName(): string {
    return this.currentPlaceholderOption?.name || 'AI Model';
  }

  // Get current placeholder text
  get currentPlaceholderText(): string {
    return this.currentPlaceholderOption?.text || '';
  }

  modelCategories = [
    {
      title: '🎬 Video Generation',
      models: [
        { name: 'Veo 3.1', value: 'veo' },
        { name: 'CogXVid', value: 'cogxvid' },
        { name: 'Veo 3.1', value: 'veo' }
      ]
    },
    {
      title: '🖼️ Image Generation',
      models: [
        { name: 'Stability 3.5', value: 'stability' },
        { name: 'DALL·E', value: 'dale' },
        { name: 'Grok Image', value: 'grokimage' }
      ]
    }
  ];

  currentPlaceholder: string = '';
  private placeholderIndex = 0;
  private charIndex = 0;
  private isDeleting = false;
  private typingTimeout?: any;
  private placeholderRotateTimer: any = null;
  private isRotating = false;
  private subs: Subscription[] = [];

  private pacmanClient = mkPacmanClient({
    rockmanId: '9718dee6f1404543ba0c8ad27c5be405',
    serverUrl: 'https://de-pacman.sam-media.com/api/v2/mstore',
    impressionNumber: 1,
    signalInterval: 1000
  });

  constructor(
    private chatService: ChatFireService,
    private sideMenusService: SideMenusService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private aiFire: AIFireService,
    private route: ActivatedRoute
  ) {
    this.form = this.fb.group({
      inputText: [''],
      selectedModel: ['stability'],
      useWebSearch: [false],
      numImages: [1]
    });

    this.o3form = this.fb.group({
      effort: ['low']
    });

    this.converter = new showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true,
      ghCodeBlocks: true,
      smoothLivePreview: true,
      extensions: [
        showdownMarkdownClassesExtension({
          imageClass: 'md-img',
          textClass: 'md-text',
          aiImageClass: 'ai-image',
          aiImageBucketHint: 'media.geniousvision.com'
        })
      ]
    });

    this.chatService.isLoading$.subscribe(loading => {
      this.isLoading = loading;
      if (loading) {
        this.pacmanClient.sendFlowEvent({ category: 'Flow', action: 'advance', label: 'loading' });
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.messagesContainer) {
      this.messagesContainer.nativeElement.addEventListener('scroll', () => {
        const container = this.messagesContainer.nativeElement;
        const threshold = 50;
        const atBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
        this.userScrolledUp = !atBottom;
        this.showScrollToBottom = !atBottom;
      });
    }
  }

  ngOnInit(): void {
    this.chatService.setChatHistoryNamespace('text').subscribe();

    this.showMainSubtitle = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      this.showMainSubtitle = false;
      this.placeholderIndex = 0;
      const firstOption = this.placeholderOptions[0];
      if (firstOption) {
        this.currentPlaceholder = firstOption.text;
        this.currentLogo = firstOption.logoUrl;
      }
      this.startTypingEffect();
      this.startPlaceholderRotation(7000);
      this.cdr.detectChanges();
    }, 7000);

    this.selectedModel$ = this.form.get('selectedModel')?.valueChanges;
    this.selectedModel$?.subscribe(() => {});

    // Handle conversation ID from route params
    this.route.queryParams.subscribe(params => {
      const conversationId = params['conversationId'] || this.id;
      if (conversationId) {
        this.loadConversationById(conversationId);
      }
    });

    if (this.id) {
      this.chatService.selectConversation(this.id);
    }

    this.subs.push(
      this.chatService.currentConversation$.subscribe(conversation => {
        if (conversation) {
          this.currentMessages = conversation.messages;
          this.scrollToBottomIfNeeded();
        }
      }),

      this.chatService.isLoading$.subscribe(loading => {
        this.isLoading = loading;
      })
    );

    const currentModel = this.modelCategories
      .flatMap(cat => cat.models)
      .find(m => m.value === this.selectedModel);

    this.selectedModelName = currentModel?.name || 'Select a Model';
  }

  ngOnDestroy(): void {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    if (this.placeholderRotateTimer) {
      clearInterval(this.placeholderRotateTimer);
    }
    if (this.videoProgressInterval) {
      clearInterval(this.videoProgressInterval);
    }
    this.subs.forEach(sub => sub.unsubscribe());
  }

  trackMsg = (_: number, m: Message) => m.id ?? (m.timestamp as any);

  startTypingEffect() {
    if (this.isRotating) return;

    const typingSpeed = 120;
    const deletingSpeed = 100;
    const pauseAfterTyping = 1500;
    const pauseAfterDeleting = 500;

    const currentOption = this.placeholderOptions[this.placeholderIndex];
    if (!currentOption) return;
    const currentString = currentOption.text || '';

    if (!this.isDeleting) {
      this.charIndex++;
      this.currentPlaceholder = currentString.substring(0, this.charIndex);
      this.cdr.detectChanges();

      if (this.charIndex === currentString.length) {
        this.isDeleting = true;
        this.typingTimeout = setTimeout(() => this.startTypingEffect(), pauseAfterTyping);
      } else {
        this.typingTimeout = setTimeout(() => this.startTypingEffect(), typingSpeed);
      }
    } else {
      this.charIndex--;
      this.currentPlaceholder = currentString.substring(0, this.charIndex);
      this.cdr.detectChanges();

      if (this.charIndex === 0) {
        this.isDeleting = false;
        this.typingTimeout = setTimeout(() => this.startTypingEffect(), pauseAfterDeleting);
      } else {
        this.typingTimeout = setTimeout(() => this.startTypingEffect(), deletingSpeed);
      }
    }
  }

  private startPlaceholderRotation(intervalMs: number = 7000): void {
    if (!Array.isArray(this.placeholderOptions) || this.placeholderOptions.length === 0) return;

    this.ngZone.runOutsideAngular(() => {
      this.placeholderRotateTimer = setInterval(() => {
        this.ngZone.run(() => {
          if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
            this.typingTimeout = undefined;
          }

          this.isRotating = true;
          this.logoFading = true;
          this.cdr.detectChanges();

          setTimeout(() => {
            this.placeholderIndex = (this.placeholderIndex + 1) % this.placeholderOptions.length;
            const nextOption = this.placeholderOptions[this.placeholderIndex];

            if (nextOption) {
              this.currentLogo = nextOption.logoUrl;
              this.currentPlaceholder = '';
              this.charIndex = 0;
              this.isDeleting = false;
            }

            this.logoFading = false;
            this.isRotating = false;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.startTypingEffect();
            }, 100);
          }, 300);
        });
      }, intervalMs);
    });
  }

  get selectedModel(): string {
    return this.form.get('selectedModel')?.value || 'stability';
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.recallPreviousMessage();
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.recallNextMessage();
    }
  }

  openAICatalog(): void {
    const ref = this.dialog.open(AiCatalogComponent, {
      panelClass: 'ai-catalog-dialog-panel',
      width: '900px',
      maxWidth: '98vw',
      height: 'auto',
      maxHeight: '95vh',
      data: {
        categories: this.modelCategories,
        selectedModel: this.selectedModel,
        selectedModelName: this.selectedModelName
      }
    });

    ref.afterClosed().subscribe((result?: { name: string; value: string }) => {
      if (!result) return;
      this.form.get('selectedModel')?.setValue(result.value);
      this.selectedModelName = result.name || 'Select a Model';
      this.cdr.markForCheck();
    });
  }

  openModelSpecDialog() {
    if (!this.selectedModel) return;

    const dialogMap: { [key: string]: () => void } = {
      'stability': () => this.openStabilityDialog(),
      'dale': () => this.openDalEDialog(),
      'veo': () => this.openVeoDialog(),
      'cogxvid': () => this.openCogXVidDialog(),
      'geminiimage': () => this.openGeminiImageDialog()
    };

    const openFn = dialogMap[this.selectedModel];
    if (openFn) {
      openFn();
    }
  }

  openStabilityDialog() {
    this.dialog.open(StabilityComponent, {
      width: '980px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'stability-dialog-panel'
    });
  }

  openDalEDialog() {
    this.dialog.open(DalEComponent, {
      width: '900px',
      maxWidth: '99vw',
      height: 'auto',
      maxHeight: '98vh',
      panelClass: 'dale-dialog-panel'
    });
  }

  openVeoDialog() {
    // Placeholder for Veo dialog
    console.log('Veo 3.1 dialog - to be implemented');
  }

  openCogXVidDialog() {
    // Placeholder for CogXVid dialog
    console.log('CogXVid dialog - to be implemented');
  }

  openGeminiImageDialog() {
    // Placeholder for Gemini Image dialog
    console.log('Gemini Image Generation dialog - to be implemented');
  }

  openVeoVideoGenerator(): void {
    this.selectedModelName = 'Veo 3.1';
    this.form.get('selectedModel')?.setValue('veo');
    this.showVideoGeneratorMenu = false;
  }

  openCogXVidGenerator(): void {
    this.selectedModelName = 'CogXVid';
    this.form.get('selectedModel')?.setValue('cogxvid');
    this.showVideoGeneratorMenu = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideVideoMenu =
      this.menuContainer?.nativeElement?.contains(target) ?? false;
    if (!clickedInsideVideoMenu) {
      this.showVideoGeneratorMenu = false;
    }
  }

  onFileSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;

    this.selectedFile = file;

    const reader = new FileReader();
    reader.onload = e => {
      this.selectedImagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  onPickImage(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;

    this.uploadImageFile = file;

    const reader = new FileReader();
    reader.onload = e => {
      this.uploadImagePreviewUrl = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  clearImage(): void {
    this.uploadImageFile = null;
    this.uploadImagePreviewUrl = null;
  }

  sendMessage(): void {
    if (this.isLoading || !this.userInput.trim()) return;

    const prompt = this.userInput;

    if (!this.messageHistory.includes(prompt)) {
      this.messageHistory.push(prompt);
    }
    this.historyIndex = -1;
    this.isRepeatPromptActive = false;

    this.userInput = '';

    const model = this.selectedModel;
    const useWeb = this.form.get('useWebSearch')?.value === true;

    // Video generation routes
    if (model === 'veo') {
      this.chatService.sendVeoVideo(prompt);
      this.resetUploadState();
      return;
    }

    if (model === 'cogxvid') {
      this.chatService.sendCogVideoX3(prompt, this.uploadImageFile);
      this.resetUploadState();
      return;
    }

    // Image generation routes
    if (model === 'stability') {
      const numImages: number = Number(this.form.get('numImages')?.value ?? 1);
      this.chatService.sendMessage(prompt, model, useWeb, undefined, numImages).subscribe();
      this.resetUploadState();
      return;
    }

    if (model === 'dale') {
      this.chatService.sendMessage(prompt, model, useWeb).subscribe();
      this.resetUploadState();
      return;
    }

    if (model === 'geminiimage') {
      // Placeholder for Gemini Image Generation
      console.log('Gemini Image Generation - to be implemented');
      this.resetUploadState();
      return;
    }

    // Default fallback
    this.chatService.sendMessage(prompt, model, useWeb).subscribe();
    this.resetUploadState();
  }

  recallPreviousMessage(): void {
    if (this.messageHistory.length === 0) return;

    if (this.historyIndex === -1) {
      this.historyIndex = this.messageHistory.length - 1;
    } else if (this.historyIndex > 0) {
      this.historyIndex--;
    }

    this.userInput = this.messageHistory[this.historyIndex];
    this.isRepeatPromptActive = true;
  }

  recallNextMessage(): void {
    if (this.messageHistory.length === 0 || this.historyIndex === -1) return;

    if (this.historyIndex < this.messageHistory.length - 1) {
      this.historyIndex++;
      this.userInput = this.messageHistory[this.historyIndex];
      this.isRepeatPromptActive = true;
    } else {
      this.historyIndex = -1;
      this.userInput = '';
      this.isRepeatPromptActive = false;
    }
  }

  cyclePromptHistory(): void {
    if (this.messageHistory.length === 0) return;

    if (this.historyIndex <= 0) {
      this.historyIndex = this.messageHistory.length - 1;
    } else {
      this.historyIndex--;
    }

    this.userInput = this.messageHistory[this.historyIndex];
    this.isRepeatPromptActive = true;
  }

  private resetUploadState(): void {
    this.selectedFile = undefined;
    this.selectedImagePreview = null;
    this.uploadImageFile = null;
    this.uploadImagePreviewUrl = null;
  }

  convertMarkdown(markdown: string): string {
    try {
      return this.converter.makeHtml(markdown || '');
    } catch {
      return markdown || '';
    }
  }

  scrollToBottom(): void {
    const container = this.messagesContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
    this.userScrolledUp = false;
    this.showScrollToBottom = false;
  }

  private scrollToBottomIfNeeded(): void {
    if (!this.userScrolledUp) {
      setTimeout(() => {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }, 100);
    }
  }

  autoResize(el: HTMLTextAreaElement): void {
    const MAX = 240;

    if (!el.value || el.value.trim().length === 0) {
      el.style.height = '';
      el.style.overflowY = 'hidden';
      el.scrollTop = 0;
      return;
    }

    const cs = window.getComputedStyle(el);
    const lineH = parseFloat(cs.lineHeight || '20') || 20;

    el.style.height = '';

    const scrollH = el.scrollHeight;
    const clientH = el.clientHeight || lineH;

    const fillRatio = scrollH / clientH;
    const shouldPreGrow = fillRatio >= 0.85;

    const desired = Math.min(scrollH + (shouldPreGrow ? lineH : 0), MAX);

    el.style.height = `${desired}px`;

    const capped = desired >= MAX;
    el.style.overflowY = capped ? 'auto' : 'hidden';

    if (capped) el.scrollTop = el.scrollHeight;
  }

  getTextareaPlaceholder(): string {
    // Special cases: image models get different placeholder
    if (this.selectedModel === 'stability' || this.selectedModel === 'dale' || this.selectedModel === 'geminiimage') {
      return `Describe the image you want…`;
    }

    // Video models
    if (this.selectedModel === 'veo' || this.selectedModel === 'cogxvid') {
      return `Describe the video you want to create…`;
    }

    // Default chat placeholder
    const modelName = this.selectedModelName || 'AI';
    return `You are now talking to ${modelName} — what's on your mind?`;
  }

  /**
   * Load a conversation by ID (used when clicking from menu)
   */
  private async loadConversationById(conversationId: string): Promise<void> {
    try {
      const conversation = await firstValueFrom(this.chatService.loadConversationById(conversationId));
      if (conversation) {
        this.cdr.detectChanges();
      }
    } catch (err) {
      console.error('[ClariMedia] Error loading conversation', err);
    }
  }
}
